import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

dayjs.extend(relativeTime);

interface ProductDetail {
  [key: string]: any;
}

interface ProductInfo {
  productId: string;
  surfaceType: string;
  productDetail: ProductDetail;
}

export interface DashboardLead {
  id: number;
  email: string;
  products: ProductInfo[];
  lastInteraction: string;
  engagement: 'hot' | 'warm' | 'cold';
  interactions: any[];
}

interface LeadsCardProps {
  leads: DashboardLead[];
  loading: boolean;
  onViewAll: () => void;
  semanticColors?: unknown;
}

/**
 * How warm a lead is, and how much of the ring is drawn for it.
 *
 * Hot and cold read as red and grey; warm has no token of its own in the
 * palette — the preset is deliberately greyscale — so it keeps a named amber.
 */
const ENGAGEMENT = {
  hot: { arc: 1, key: 'ENGAGEMENT_HOT', ring: 'text-destructive', badge: 'border-destructive/30 bg-destructive/10 text-destructive' },
  warm: { arc: 0.6, key: 'ENGAGEMENT_WARM', ring: 'text-warning', badge: 'border-warning/30 bg-warning/10 text-warning' },
  cold: { arc: 0.25, key: 'ENGAGEMENT_COLD', ring: 'text-muted-foreground', badge: 'border-border bg-muted text-muted-foreground' },
} as const;

/** The arc around the initial: how engaged this lead is, at a glance. */
const EngagementRing = ({ engagement, size = 36 }: { engagement: keyof typeof ENGAGEMENT; size?: number }) => {
  const { arc, ring } = ENGAGEMENT[engagement];
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * arc;

  return (
    <svg width={size} height={size} className={cn('absolute inset-0 -rotate-90', ring)}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} opacity={0.15} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeLinecap="round"
      />
    </svg>
  );
};

export const LeadsCard = ({ leads, loading, onViewAll }: LeadsCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">{t('PAGES.DASHBOARD.LATEST_LEADS')}</CardTitle>
        <CardAction>
          <Button variant="ghost" size="sm" onClick={onViewAll} className="h-7 text-xs">
            {t('PAGES.DASHBOARD.VIEW_ALL')}
          </Button>
        </CardAction>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-13" />
            ))}
          </div>
        ) : !leads.length ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('PAGES.DASHBOARD.NO_LEADS_YET')}</p>
        ) : (
          <div className="flex flex-col">
            {leads.map((lead) => {
              const config = ENGAGEMENT[lead.engagement];
              return (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => navigate('/visualizer/engagement/leads')}
                  className="-mx-2 flex items-center gap-3 px-2 py-2 text-start transition-colors hover:bg-accent/50"
                >
                  <span className="relative flex size-9 shrink-0 items-center justify-center">
                    <EngagementRing engagement={lead.engagement} />
                    <span className="text-xs font-medium uppercase">{lead.email.slice(0, 2)}</span>
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{lead.email}</span>
                    <span className="mt-0.5 flex items-center gap-2">
                      <Badge variant="outline" className={cn('px-1.5 py-0 text-[10px]', config.badge)}>
                        {t(`PAGES.DASHBOARD.${config.key}`)}
                      </Badge>
                      <span className="truncate text-xs text-muted-foreground">
                        {lead.interactions.length} {t('PAGES.DASHBOARD.INTERACTIONS')}
                        {lead.lastInteraction ? ` · ${dayjs(lead.lastInteraction).fromNow()}` : ''}
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
